import React, { useEffect, useState, useRef } from "react";
import "../../../../../css/CashflowModeller2/Inputs/Inputs.css";
import "../../../../../css/CashflowModeller2/Inputs/Input2.css";
import ArrowDown from "../Buttons/ArrowDown";
import Dropdown from "../Buttons/Dropdown";
import useClickOutside from "../../Hooks/UseClickOutside";
import { useFormattedNumber } from "../../Hooks/UseFormattedNumber";

const   Input2 = ({
  isDisabled,
  type = "text",
  label,
  value,
  onChange,
  isError,
  options = [],
  isCheckBoxOptions,
  onUnitChange,
  readOnly,
  placeholderText,
  currency = false,
  currentUnit,
  per = false,
}) => {
  const [status, setStatus] = useState("default");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [unit, setUnit] = useState(currentUnit || "Annual");
  const dropdownRef = useRef();
  const [isFocused, setIsFocused] = useState(false);

  const {
    rawValue,
    formattedValue,
    handleChange: handleFormattedChange,
  } = useFormattedNumber(value, type);

  useClickOutside(dropdownRef, () => setIsDropdownOpen(false));

  useEffect(() => {
    if (type !== "number") return;
    // Update only if number, for text just use raw
    // handleFormattedChange({ target: { value: value } });
    handleFormattedChange(value);
  }, [value]);

  /**
   * Handles the click event on the arrow icon.
   * Toggles the dropdown open state if the input is not disabled.
   *
   * @function
   * @returns {void}
   */
  const handleArrowClick = () => {
    if (!isDisabled) setIsDropdownOpen((prev) => !prev);
  };

  /**
   * Handles the change of unit selection from a dropdown.
   * Updates the unit state, triggers the optional onUnitChange callback,
   * and closes the dropdown menu.
   *
   * @param {any} opt - The selected unit option.
   */
  const handleUnitChange = (opt) => {
    setUnit(opt);
    onUnitChange?.(opt);
    setIsDropdownOpen(false);
  };

  /**
   * Handles the selection of an option from the dropdown.
   * Closes the dropdown and triggers the onChange callback with the selected option.
   *
   * @param {*} opt - The selected option from the dropdown.
   */
  const handleOptionSelect = (opt) => {
    setIsDropdownOpen(false);
    onChange?.(opt);
  };

  /**
   * Toggles the selection state of a checkbox option.
   *
   * If the given option is already selected, it will be removed from the selection.
   * If the option is not selected, it will be added to the selection.
   * Updates the selected options state and triggers the optional onChange callback.
   *
   * @param {*} opt - The option to toggle in the selection.
   */
  const handleCheckboxToggle = (opt) => {
    const updated = selectedOptions.includes(opt)
      ? selectedOptions.filter((o) => o !== opt)
      : [...selectedOptions, opt];
    setSelectedOptions(updated);
    onChange?.(updated);
  };

  /**
   * Handles changes to the input field and triggers the onChange callback with the appropriate value.
   * For number inputs, it uses a custom formatting handler before passing the value.
   * For other input types, it passes the raw value from the event target.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} e - The change event from the input field.
   */
  const handleLocalChange = (e) => {
    if (type === "number") {
      const raw = handleFormattedChange(e);
      onChange?.(raw);
    } else {
      onChange?.(e.target.value);
    }
  };

  /**
   * Formats the display value based on the input type and formatting options.
   *
   * - If the input type is not "number", returns the raw value or an empty string.
   * - If there is no formatted value, returns an empty string.
   * - If a currency flag is set, prefixes the formatted value with "£".
   * - If a percentage flag is set, appends "%" to the formatted value.
   * - Otherwise, returns the formatted value as is.
   *
   * @returns {string} The formatted display value for rendering in the UI.
   */
  const formatDisplayValue = () => {
    if (type !== "number") return value || "";
    if (!formattedValue) return "";
    if (currency) return `£${formattedValue}`;
    if (per) return `${formattedValue}%`;
    return formattedValue;
  };

  return (
    <div className={`${label ? "input-label-container" : ""}`}>
      {label && <div className="Label">{label}</div>}

      <div
        className={`input-container ${isDisabled ? "disabled" : ""} ${status} ${
          isError ? "error" : ""
        } ${readOnly ? "readOnly" : ""}`}
        ref={dropdownRef}
      >
        <input
          type="text"
          inputMode={type === "number" ? "decimal" : "text"}
          pattern={type === "number" ? "\\d*\\.?\\d*" : undefined}
          value={isFocused ? rawValue : formatDisplayValue()}
          placeholder={
            placeholderText
              ? placeholderText
              : type === "number"
              ? "8,000"
              : "William"
          }
          onChange={handleLocalChange}
          onFocus={() => !readOnly && setStatus("selected")}
          onBlur={() => {
            setStatus("default");
            setIsFocused(false);
          }}
          className="input-field Input"
          disabled={isDisabled}
          readOnly={
            typeof readOnly === "boolean" ? readOnly : options.length > 0
          }
        />

        {options.length > 0 && !readOnly && !isDisabled && (
          <div
            className="input-unit-display input-icon-container arrow-icon"
            onClick={handleArrowClick}
          >
            {rawValue && options.some((opt) => opt.value === unit) && (
              <div className="Label">
                {options.find((opt) => opt.value === unit)?.display}
              </div>
            )}
            <ArrowDown />
          </div>
        )}

        {isDropdownOpen && (
          <div className="input-dropdown-panel">
            {options.map((opt) => {
              const handleClick = () => {
                if (isCheckBoxOptions) {
                  handleCheckboxToggle(opt.value);
                } else if (type === "number") {
                  handleUnitChange(opt.value);
                } else {
                  handleOptionSelect(opt.value);
                }
              };

              return (
                <div
                  key={opt.value}
                  className={`dropdown-option-row ${
                    isCheckBoxOptions ? "with-checkbox" : ""
                  }`}
                  onClick={handleClick}
                >
                  {isCheckBoxOptions && (
                    <input
                      type="checkbox"
                      checked={selectedOptions.includes(opt.value)}
                      readOnly
                    />
                  )}
                  <Dropdown
                    labelText={opt.label}
                    className={` ${unit === opt.value ? "selected" : ""}`}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Input2;
